/**
 * Адрес в строке браузера как источник того, какой экран показан (тикет T18b).
 *
 * Кнопка «назад» обязана работать: человек нажимает её не задумываясь, и экран, который
 * на неё не отзывается, читается как зависший. Поэтому адрес ведущий, а состояние
 * ведомое, а не наоборот.
 */
import { useCallback, useSyncExternalStore } from 'react';
import { pathOf, routeOf } from './route.js';
import type { Route } from './route.js';

const listeners = new Set<() => void>();

const tell = (): void => {
  for (const listener of listeners) listener();
};

const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener);
  // Своих переходов `popstate` не даёт — о них рассказываем сами, из `go`.
  window.addEventListener('popstate', listener);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('popstate', listener);
  };
};

const currentPath = (): string => window.location.pathname;

export const useRoute = (): readonly [Route, (route: Route, replace?: boolean) => void] => {
  // Снимок — сама строка пути, а не разобранный адрес: React сравнивает снимки по
  // равенству, и новый объект на каждой проверке заставлял бы его перерисовывать экран
  // без конца.
  const path = useSyncExternalStore(subscribe, currentPath, () => '/');

  const go = useCallback((route: Route, replace = false): void => {
    const next = pathOf(route);
    if (next === window.location.pathname) return;
    if (replace) window.history.replaceState(null, '', next);
    else window.history.pushState(null, '', next);
    tell();
  }, []);

  return [routeOf(path), go] as const;
};
